import { datadogRum, type RumActionEventDomainContext } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom";

import { RootForegroundNotificiationController } from "helpers/pushNotification.utilities";

import { getClickActionTestId } from "utilities/analytics/rumTestId";
import "utilities/api/setupApix";
import {
    getCurrentEnvironment,
    getCurrentSentryEnvironment,
} from "utilities/environment/environment";
import { initContext } from "utilities/init/bootstrap";
import "utilities/jobPicker/applyJobPickerModeFromUrl";
import "utilities/webview/setupMobileMock";

import { registerDragAndDropPlugins } from "commonComponents/utilities/DragAndDrop/DragAndDrop";
import { RootFocusObserver } from "commonComponents/utilities/Focus/RootFocusObserver";

import "./styles/globalBase.less";

import { App } from "./App";

window.rootFocusObserver = new RootFocusObserver();
window.rootForegroundNotificationController = new RootForegroundNotificiationController();

const environment: string = getCurrentEnvironment();

// Datadog RUM and Sentry config comes from the anonymous, cacheable APIX endpoint
// (GET /apix/v2/context/init), fetched by an inline script in index.html rather than the
// render-blocking, session-aware JScriptGlobalsHandler.ashx. `utilities/init/bootstrap` awaits
// it, so importing that module defers everything below until the config resolves and telemetry
// initializes pre-render, letting RUM capture full page-load timings. `env`/`environment` stay
// hostname-derived. The await must stay in bootstrap.ts — see the note there. ADO 282448.
//
// `release` is deliberately not part of that payload: the endpoint is cached for up to a day,
// so a runtime-fetched release could tag telemetry with the previous deploy. It is stamped into
// the bundle at build time instead, where it can never disagree with the code running. ADO 285393.
const release: string | undefined = import.meta.env.VITE_BUILD_ID || undefined;

// Benign browser errors that aren't actionable app bugs — filtered from both
// RUM and Sentry to reduce noise. NetworkError is broad so only Sentry ignores it.
const commonIgnoredErrors = [
    "ResizeObserver loop",
    // Salesforce ESW chat bundle: deferred callbacks reference an
    // `embeddedservice_bootstrap` global that is gone by the time the
    // user has logged out/navigated away. The SDK is minified and
    // can't be patched.
    "embeddedservice_bootstrap",
    // Microsoft Word saves documents as HTML with onclick handlers that call
    // msoCommentHide / msoCommentShow. When Word-generated HTML is rendered
    // in BT (e.g. rich text fields), clicking comment annotations fires these
    // ReferenceErrors. The functions are Office-internal and can't be stubbed.
    "msoComment",
];

if (initContext.datadog.enabled) {
    datadogRum.init({
        applicationId: initContext.datadog.applicationId,
        clientToken: initContext.datadog.clientToken,
        site: initContext.datadog.site,
        service: "buildertrend.net",
        env: environment,
        // Replay and trace rates are percentages of the sessions sessionSampleRate collected, not
        // of all users — they multiply with it. Both default to 100 when omitted, so raising the
        // session rate without lowering these scales replay and APM volume by the same factor.
        sessionSampleRate: initContext.datadog.sessionSampleRate,
        sessionReplaySampleRate: initContext.datadog.sessionReplaySampleRate,
        traceSampleRate: initContext.datadog.traceSampleRate,
        trackUserInteractions: initContext.datadog.trackUserInteractions,
        trackResources: true,
        trackLongTasks: true,
        allowedTracingUrls: initContext.datadog.enableTracing
            ? [/https:\/\/.*buildertrend\.net/]
            : undefined,
        defaultPrivacyLevel: "mask-user-input",
        version: release,
        enableExperimentalFeatures: ["feature_flags"],
        beforeSend: (event, context) => {
            if (
                event.type === "error" &&
                commonIgnoredErrors.some((msg) => event.error?.message?.includes(msg))
            ) {
                return false;
            }
            if (event.type === "action" && event.action.type === "click") {
                const testId = getClickActionTestId(
                    (context as RumActionEventDomainContext).events,
                );
                if (testId) {
                    event.context = { ...event.context, testId };
                }
            }
            return true;
        },
    });
    datadogRum.startSessionReplayRecording();
}

const sentryEnvironment: string = getCurrentSentryEnvironment();

if (initContext.sentry.enabled) {
    const integrations: any[] = [
        Sentry.browserTracingIntegration({
            beforeStartSpan: (context) => {
                context.name = location.pathname
                    .replace(/\/[a-f0-9]{32}/g, "/<hash>")
                    .replace(/\/\d+/g, "/<digits>");
                return context;
            },
        }),
        Sentry.httpClientIntegration(),
    ];

    Sentry.init({
        dsn: initContext.sentry.dsn,
        environment: sentryEnvironment,
        ignoreErrors: [...commonIgnoredErrors, "NetworkError"],
        denyUrls: [/maps\.googleapis\.com\/maps-api-v3\//, /\/ESWBuildertrendChat\d+\//],
        integrations: integrations,
        tracesSampleRate: initContext.sentry.tracesSampleRate,
        beforeBreadcrumb: (breadcrumb, hint) => {
            if (breadcrumb.category === "ui.click" && hint && hint.event) {
                const { target } = hint.event;
                if (target && target.dataset && target.dataset.testid) {
                    breadcrumb.message += ` data-testid['${target.dataset.testid}']`;
                }
            }

            return breadcrumb;
        },
        release: release,
    });
}

// Register SortableJS Plugins
registerDragAndDropPlugins();

// path starts with /app/
const isSinglePageApp = window.location.pathname.toLowerCase().startsWith("/app");
const reactSinglePageAppRoot = document.getElementById("root");

// Please do not add any more code to this file, do full react conversion instead.

// TODO: Remove all instances of useStickyHeaderWithMainNav(); once we are on SPA.

if (isSinglePageApp && reactSinglePageAppRoot) {
    ReactDOM.render(<App />, reactSinglePageAppRoot);
}
